import { router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { analyticsMissionPath } from "@/routes"
import type {
  MissionAnalyticsCreatorsPage,
  MissionAnalyticsFunnelBucket,
} from "@/types/mission-analytics"

/**
 * OKS-63 — Paginated per-creator table for the per-mission deep-dive.
 *
 * Renders one row per collab on the mission (25/page). Columns:
 *   1. Creator (TikTok handle with link to tiktok.com/@handle)
 *   2. State (`Badge`)
 *   3. Days since assigned
 *   4. Submission count (≤3 — mission cap from OKS-35)
 *
 * Mobile reflow: stacked cards (mirrors the top-missions reflow).
 *
 * Pagination via the Inertia partial reload `only: ['creators_page']`.
 * Prev/Next buttons preserve scroll so the deep-dive header stays
 * stable on page change.
 */
export interface AnalyticsCreatorsTableProps {
  creatorsPage: MissionAnalyticsCreatorsPage
  missionId: number
  className?: string
}

export function AnalyticsCreatorsTable({
  creatorsPage,
  missionId,
  className,
}: AnalyticsCreatorsTableProps) {
  const { t } = useTranslation()

  const isEmpty = creatorsPage.rows.length === 0
  const { pagination } = creatorsPage

  return (
    <Card
      data-testid="analytics-detail-creators-table"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
    >
      <CardHeader className="p-0">
        <CardTitle className="text-foreground text-base font-semibold">
          {t("analytics.creators.title")}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {isEmpty ? (
          <EmptyState />
        ) : (
          <>
            <DesktopTable
              rows={creatorsPage.rows}
              className="hidden md:block"
            />
            <MobileCards rows={creatorsPage.rows} className="md:hidden" />
            <Pagination
              pagination={pagination}
              missionId={missionId}
              className="mt-4"
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface RowsProps {
  rows: MissionAnalyticsCreatorsPage["rows"]
  className?: string
}

function DesktopTable({ rows, className }: RowsProps) {
  const { t } = useTranslation()
  return (
    <div
      className={className}
      data-testid="analytics-detail-creators-table-desktop"
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("analytics.creators.columns.handle")}</TableHead>
            <TableHead>{t("analytics.creators.columns.state")}</TableHead>
            <TableHead className="text-right">
              {t("analytics.creators.columns.days_since_assigned")}
            </TableHead>
            <TableHead className="text-right">
              {t("analytics.creators.columns.submission_count")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.collab_id}
              data-testid={`analytics-detail-creator-row`}
            >
              <TableCell>
                <HandleCell handle={row.tiktok_handle} />
              </TableCell>
              <TableCell>
                <StateBadge state={row.state} />
              </TableCell>
              <TableCell className="text-right">
                {row.days_since_assigned}
              </TableCell>
              <TableCell className="text-right">
                {row.submission_count}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function MobileCards({ rows, className }: RowsProps) {
  const { t } = useTranslation()
  return (
    <ul
      className={cn("flex flex-col gap-3", className)}
      data-testid="analytics-detail-creators-table-mobile-list"
    >
      {rows.map((row) => (
        <li
          key={row.collab_id}
          className="bg-muted/30 space-y-2 rounded-lg border p-3"
          data-testid={`analytics-detail-creator-card`}
        >
          <div className="flex items-center justify-between">
            <HandleCell handle={row.tiktok_handle} />
            <StateBadge state={row.state} />
          </div>
          <dl className="text-muted-foreground grid grid-cols-2 gap-2 text-xs">
            <div>
              <dt>{t("analytics.creators.columns.days_since_assigned")}</dt>
              <dd className="text-foreground font-medium">
                {row.days_since_assigned}
              </dd>
            </div>
            <div>
              <dt>{t("analytics.creators.columns.submission_count")}</dt>
              <dd className="text-foreground font-medium">
                {row.submission_count}
              </dd>
            </div>
          </dl>
        </li>
      ))}
    </ul>
  )
}

function HandleCell({ handle }: { handle: string | null }) {
  const { t } = useTranslation()
  if (!handle) {
    return (
      <span className="text-muted-foreground">
        {t("analytics.creators.handle_missing")}
      </span>
    )
  }
  return (
    <a
      href={`https://www.tiktok.com/@${handle}`}
      target="_blank"
      rel="noreferrer noopener"
      className="text-primary font-medium hover:underline"
    >
      @{handle}
    </a>
  )
}

function StateBadge({
  state,
}: {
  state: MissionAnalyticsFunnelBucket["state"]
}) {
  const { t } = useTranslation()
  // Reuse the funnel bucket labels for state — same i18n key tree.
  const label =
    state === "to_ship" || state === "shipped" || state === "delivered"
      ? t("analytics.funnel.buckets.shipping")
      : t(`analytics.funnel.buckets.${state}`)
  return <Badge variant="secondary">{label}</Badge>
}

const CREATORS_RELOAD_ONLY = ["creators_page"]

function Pagination({
  pagination,
  missionId,
  className,
}: {
  pagination: MissionAnalyticsCreatorsPage["pagination"]
  missionId: number
  className?: string
}) {
  const { t } = useTranslation()
  if (pagination.pages <= 1) return null

  const goto = (page: number) => {
    router.get(analyticsMissionPath(missionId, { page }), undefined, {
      only: CREATORS_RELOAD_ONLY,
      preserveState: false,
      preserveScroll: true,
    })
  }

  return (
    <nav
      className={cn("flex items-center justify-between gap-2", className)}
      data-testid="analytics-detail-pagination"
      aria-label={t("analytics.creators.section_aria")}
    >
      <Button
        variant="outline"
        size="sm"
        disabled={pagination.prev == null}
        onClick={() => pagination.prev != null && goto(pagination.prev)}
        data-testid="analytics-detail-pagination-prev"
      >
        {pagination.page} / {pagination.pages}
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={pagination.next == null}
        onClick={() => pagination.next != null && goto(pagination.next)}
        data-testid="analytics-detail-pagination-next"
      >
        →
      </Button>
    </nav>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="analytics-detail-creators-empty"
      className="text-muted-foreground flex flex-col items-center gap-2 py-8 text-sm"
    >
      <p className="text-foreground text-sm font-medium">
        {t("analytics.creators.empty.title")}
      </p>
      <p>{t("analytics.creators.empty.body")}</p>
    </div>
  )
}
